/**
 * Small fetch client for the Cardnd backend: JSON (or multipart) bodies, Bearer token, timeouts,
 * zod-checked responses, and every failure turned into an `ApiError` with a message in the current language.
 */
import type { z } from 'zod';
import { getLocale, messageMap, ns } from '@/i18n';
import { ApiError, type ApiErrorCode } from '../api';

export type Auth = 'none' | 'optional' | 'required';
type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions<T> {
  query?: Record<string, QueryValue>;
  json?: unknown;
  form?: FormData;
  /** `required`: refuse without a session (no request); `optional`: send the token when there is one. */
  auth?: Auth;
  schema?: z.ZodType<T>;
  /** Per-call messages by error code or HTTP status (they win over the server's message), current language. */
  messages?: Partial<Record<ApiErrorCode | number, string>>;
  timeoutMs?: number;
  /** Extra headers (e.g. the Meta event id of a booking). */
  headers?: Record<string, string>;
}

export interface HttpClientOptions {
  /** API origin. Empty string = same origin (dev proxy). */
  baseUrl: string;
  /** The live token, or null (none, or an expired one: reading it ends the session, see `sessionEnded`). */
  getToken: () => string | null;
  /**
   * True when the user's session ended during this visit and nobody signed in since (CK-02): an authed call
   * (`optional` or `required`) is then refused without being sent, never made as a guest's.
   */
  sessionEnded?: () => boolean;
  /** A request that carried a token was refused with a 401, outside the auth endpoints (expired or revoked session). */
  onUnauthorized?: () => void;
  timeoutMs?: number;
  fetchImpl?: typeof fetch;
}

export interface RawResponse {
  status: number;
  body: unknown;
}

export interface HttpClient {
  baseUrl: string;
  request<T = unknown>(method: string, path: string, options?: RequestOptions<T>): Promise<T>;
  /** Same request, but an HTTP error is returned (status + body) instead of thrown; network errors still throw. */
  raw(method: string, path: string, options?: Omit<RequestOptions<unknown>, 'schema'>): Promise<RawResponse>;
  get<T = unknown>(path: string, options?: RequestOptions<T>): Promise<T>;
  post<T = unknown>(path: string, options?: RequestOptions<T>): Promise<T>;
  patch<T = unknown>(path: string, options?: RequestOptions<T>): Promise<T>;
  delete<T = unknown>(path: string, options?: RequestOptions<T>): Promise<T>;
  /** A file (voucher PDF), with the same session rules: the bytes of a 2xx answer, else the status and no blob. */
  blob(path: string, options?: Omit<RequestOptions<unknown>, 'schema' | 'json' | 'form'>): Promise<BlobResponse>;
}

export interface BlobResponse {
  status: number;
  blob: Blob | null;
}

/** Auth endpoints: a 401 there means wrong credentials, not an expired session (legacy `isAuthEndpoint`). */
const AUTH_ENDPOINT_MARKERS = ['/auth/login', '/auth/register', '/auth/google', '/forgot-password', '/reset-password'];
export const isAuthEndpoint = (path: string): boolean => AUTH_ENDPOINT_MARKERS.some((m) => path.includes(m));

/** The session expired: the call is refused before it goes out (CK-02, AU-02). */
export const sessionExpiredError = () =>
  new ApiError(MESSAGES.sessionExpired, 'unauthorized', { status: 401, serverCode: 'SESSION_EXPIRED' });

/** Default messages by error code, read in the current language at each access (`MESSAGES.network`). */
export const MESSAGES = messageMap(ns.errors, {
  network: 'http.network',
  timeout: 'http.timeout',
  unauthorized: 'http.unauthorized',
  sessionExpired: 'http.sessionExpired',
  forbidden: 'http.forbidden',
  readOnly: 'http.readOnly',
  not_found: 'http.not_found',
  unavailable: 'http.unavailable',
  invalid: 'http.invalid',
  tooMany: 'http.tooMany',
  badResponse: 'http.badResponse',
});

/**
 * Backend messages are a mix of French and English. In French, French ones are shown as they are and
 * English ones are replaced by the per-call or default message. In English, the server's text is never
 * shown (it is French or technical): the per-call or default English message is (Decision 96).
 */
const FRENCH =
  /[àâçéèêëîïôûùüœ’«»]|\b(?:le|la|les|des|du|une|un|pour|est|pas|vous|votre|cette|ce|réservation|merci|déjà|aucun|aucune|ne|n')\b/i;
export const isFrench = (s: string) => FRENCH.test(s);

/** A server text to show as is: French text while the interface is French; never in English. */
export const showableServerText = (s: string | undefined | null): string | undefined =>
  s && getLocale() === 'fr' && isFrench(s) ? s.trim() : undefined;

/** The backend's message, when it can be shown in the current language (see above). */
export function serverMessage(body: unknown): string | undefined {
  if (!body || typeof body !== 'object' || getLocale() !== 'fr') return undefined;
  const b = body as Record<string, unknown>;
  const m = [b.message, b.error, b.msg].find((x): x is string => typeof x === 'string' && x.trim().length > 0);
  return m && isFrench(m) ? m.trim() : undefined;
}

const serverCode = (body: unknown): string | undefined => {
  if (!body || typeof body !== 'object') return undefined;
  const c = (body as Record<string, unknown>).code;
  return typeof c === 'string' ? c : undefined;
};

/**
 * The local dev server's read-only lock on real data (`npm run dev:prod-readonly`, scripts/dev/readOnlyGuard.mjs)
 * answers 403 `READ_ONLY`: say so, whatever the call, instead of a sign-in or access message. Never sent by the backend.
 */
function throwIfReadOnly(status: number, body: unknown): void {
  if (serverCode(body) === 'READ_ONLY') throw new ApiError(MESSAGES.readOnly, 'invalid', { status, serverCode: 'READ_ONLY' });
}

function codeFor(status: number): ApiErrorCode {
  if (status === 401 || status === 403) return 'unauthorized';
  if (status === 404) return 'not_found';
  if (status === 429 || status >= 500) return 'unavailable';
  return 'invalid';
}

function defaultMessage(status: number, code: ApiErrorCode): string {
  if (status === 403) return MESSAGES.forbidden;
  if (status === 429) return MESSAGES.tooMany;
  return MESSAGES[code];
}

function buildUrl(baseUrl: string, path: string, query?: Record<string, QueryValue>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return `${baseUrl.replace(/\/$/, '')}${path}${s ? `?${s}` : ''}`;
}

async function readBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { raw: text.slice(0, 200) };
  }
}

export function createHttpClient(opts: HttpClientOptions): HttpClient {
  const fetchImpl = opts.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
  const defaultTimeout = opts.timeoutMs ?? 15_000;

  async function send(
    method: string,
    path: string,
    o: RequestOptions<unknown>,
    read: 'body' | 'blob' = 'body',
  ): Promise<{ res: Response; data: unknown; token: string | null }> {
    const auth = o.auth ?? 'optional';
    const token = auth === 'none' ? null : opts.getToken();
    // The user believed they were signed in: never send this call as a guest's (an expired token was just dropped).
    if (auth !== 'none' && !token && opts.sessionEnded?.()) throw sessionExpiredError();
    if (auth === 'required' && !token) {
      throw new ApiError(o.messages?.unauthorized ?? MESSAGES.unauthorized, 'unauthorized', { status: 401 });
    }
    const headers: Record<string, string> = { Accept: 'application/json', ...o.headers };
    if (token) headers.Authorization = `Bearer ${token}`;
    let body: BodyInit | undefined;
    if (o.form) body = o.form;
    else if (o.json !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(o.json);
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), o.timeoutMs ?? defaultTimeout);
    let res: Response;
    try {
      res = await fetchImpl(buildUrl(opts.baseUrl, path, o.query), {
        method,
        headers,
        body,
        signal: controller.signal,
      });
    } catch (e) {
      const timedOut = controller.signal.aborted;
      throw new ApiError(timedOut ? MESSAGES.timeout : (o.messages?.network ?? MESSAGES.network), 'network', {
        serverCode: timedOut ? 'TIMEOUT' : (e as Error)?.name,
      });
    } finally {
      clearTimeout(timer);
    }
    // AU-02: a refused token ends the session (not on the auth endpoints, whose 401 is «wrong credentials»).
    if (res.status === 401 && token && !isAuthEndpoint(path)) opts.onUnauthorized?.();
    const data = read === 'blob' && res.ok ? null : await readBody(res).catch(() => null);
    return { res, data, token };
  }

  async function request<T>(method: string, path: string, o: RequestOptions<T> = {}): Promise<T> {
    const { res, data } = await send(method, path, o as RequestOptions<unknown>);
    if (!res.ok) {
      throwIfReadOnly(res.status, data);
      const code = codeFor(res.status);
      const message =
        o.messages?.[res.status] ?? o.messages?.[code] ?? serverMessage(data) ?? defaultMessage(res.status, code);
      throw new ApiError(message, code, { status: res.status, serverCode: serverCode(data) });
    }
    if (!o.schema) return data as T;
    const parsed = o.schema.safeParse(data);
    if (!parsed.success) {
      console.warn(`[api] unexpected response for ${method} ${path}`, parsed.error.issues.slice(0, 3));
      throw new ApiError(MESSAGES.badResponse, 'unavailable', { status: res.status, serverCode: 'BAD_RESPONSE' });
    }
    return parsed.data;
  }

  return {
    baseUrl: opts.baseUrl,
    request,
    raw: async (method, path, o = {}) => {
      const { res, data } = await send(method, path, o);
      if (!res.ok) throwIfReadOnly(res.status, data);
      return { status: res.status, body: data };
    },
    get: (path, o) => request('GET', path, o),
    post: (path, o) => request('POST', path, o),
    patch: (path, o) => request('PATCH', path, o),
    delete: (path, o) => request('DELETE', path, o),
    blob: async (path, o = {}) => {
      const { res } = await send('GET', path, o, 'blob');
      return { status: res.status, blob: res.ok ? await res.blob() : null };
    },
  };
}
